import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowLeft, Star, Megaphone, Sparkles, LogOut, LayoutDashboard, AlertCircle } from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import { useAuth } from '../hooks/useAuth';

// Smooth-scroll to the plans section. Works whether we're already on /pricing
// (just scroll) or coming from elsewhere (React Router navigates, then this
// fires once the element exists in the DOM).
function scrollToPlans() {
  const el = document.getElementById('plans');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    // Element not in the DOM yet (cross-page navigation). Poll briefly.
    let tries = 0;
    const iv = setInterval(() => {
      const e = document.getElementById('plans');
      if (e) { e.scrollIntoView({ behavior: 'smooth', block: 'start' }); clearInterval(iv); }
      if (++tries > 20) clearInterval(iv);
    }, 100);
  }
}

const LINKS = [
  { to: '/features',    label: 'Features',  icon: Star },
  { to: '/comparison',  label: 'Compare' },
  { to: '/analyze',     label: 'Free Tools', icon: Sparkles, accent: 'emerald' },
  { to: '/pricing',     label: 'Pricing' },
  { to: '/advertise',   label: 'Advertise', icon: Megaphone, accent: 'violet' },
];

function linkClass({ isActive }, accent) {
  const hover = accent === 'emerald' ? 'hover:text-emerald-300'
              : accent === 'violet'  ? 'hover:text-violet-300'
              :                        'hover:text-white';
  const active = isActive
    ? (accent === 'emerald' ? 'text-emerald-300' : accent === 'violet' ? 'text-violet-300' : 'text-white')
    : 'text-gray-400';
  return `text-sm ${active} ${hover} transition-colors flex items-center gap-1.5`;
}

/**
 * Shared navigation for all unauthenticated/public pages.
 * Drop into any public page so visitors can jump between Features/Compare/Pricing/etc
 * without bouncing back to the home page first.
 */
export default function PublicNav() {
  const [open, setOpen] = useState(false);
  // Auth-aware CTAs. Three states:
  //  - logged out: Sign In + Start Free Trial (existing behavior)
  //  - logged in, real tier (starter/trader/pro/elite/trial): Dashboard + Logout
  //  - logged in, tier='pending': Complete Signup (highlighted) + Logout
  // The pending case was the bug — previously a pending user clicked "Sign In"
  // from this nav and got stuck in an infinite redirect loop because
  // PublicRoute sent them back to / which the gate sent back to /pricing.
  const { isLoggedIn, user, logout } = useAuth();
  const location = useLocation();
  const isPending = user?.tier === 'pending';
  const onPricing = location.pathname === '/pricing';

  const handleLogout = (closeMenu) => {
    if (closeMenu) setOpen(false);
    logout?.();
  };

  // "Complete Subscription" / "Start Free Trial" → pick a plan. If already on
  // /pricing, smooth-scroll. Otherwise navigate + scroll once mounted.
  const handlePickPlan = (closeMenu) => {
    if (closeMenu) setOpen(false);
    if (onPricing) {
      scrollToPlans();
    } else {
      // Navigate to /pricing — the Link's `to` prop handles the route change,
      // we just queue the scroll after mount.
      setTimeout(scrollToPlans, 50);
    }
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0b0f]/90 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2.5">
          <img src={logo} alt="haythix" className="w-9 h-9" />
          <span className="font-bold text-white text-lg leading-none">haythix</span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-8">
          {LINKS.map(({ to, label, icon: Icon, accent }) => (
            <NavLink key={to} to={to} className={(s) => linkClass(s, accent)}>
              {Icon && <Icon className="w-3.5 h-3.5" />} {label}
            </NavLink>
          ))}
        </div>

        {/* Right side — auth-aware CTAs */}
        <div className="hidden md:flex items-center gap-3">
          {!isLoggedIn && (
            <>
              <Link
                to="/login"
                className="text-sm text-gray-400 hover:text-white transition-colors px-4 py-2 flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" /> Sign In
              </Link>
              {/* Start Free Trial defaults to Starter plan with trial path —
                  the post-signup handler in RegisterPage reads `?plan=starter
                  &path=trial`, calls createTrialCheckout('starter'), and lands
                  the user on Stripe Checkout. This is the proven happy path
                  from before launch. Users can change the plan on /pricing if
                  Starter doesn't fit, but defaulting eliminates a dead-end
                  step that frustrates first-time visitors. */}
              <Link
                to="/register?plan=starter&path=trial"
                className="text-sm font-semibold bg-amber-400 text-gray-900 px-4 py-2 rounded-xl hover:bg-amber-300 transition-colors"
              >
                Start Free Trial
              </Link>
            </>
          )}
          {isLoggedIn && isPending && (
            <>
              <span className="text-xs text-gray-500 hidden lg:inline">
                {user?.email}
              </span>
              <button
                type="button"
                onClick={() => handleLogout(false)}
                className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-2 flex items-center gap-1.5"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
              {/* When on /pricing already, scroll-to-plans is the right action.
                  Use a button (not Link) so we can fire scrollIntoView in onClick
                  without the dead navigation. The Link version always re-routed
                  to /pricing — same URL — so nothing visible happened. */}
              <button
                type="button"
                onClick={() => handlePickPlan(false)}
                className="text-sm font-semibold bg-amber-400 text-gray-900 px-4 py-2 rounded-xl hover:bg-amber-300 transition-colors flex items-center gap-1.5"
              >
                <AlertCircle className="w-4 h-4" /> {onPricing ? 'Pick a Plan' : 'Complete Subscription'}
              </button>
            </>
          )}
          {isLoggedIn && !isPending && (
            <>
              <span className="text-xs text-gray-500 hidden lg:inline">
                {user?.email}
              </span>
              <button
                type="button"
                onClick={() => handleLogout(false)}
                className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-2 flex items-center gap-1.5"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
              <Link
                to="/"
                className="text-sm font-semibold bg-amber-400 text-gray-900 px-4 py-2 rounded-xl hover:bg-amber-300 transition-colors flex items-center gap-1.5"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          type="button"
          className="md:hidden text-gray-300 p-2 -mr-2"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div className="md:hidden border-t border-white/5 bg-[#0a0b0f]/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
            {LINKS.map(({ to, label, icon: Icon, accent }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={(s) => `${linkClass(s, accent)} py-1.5`}
              >
                {Icon && <Icon className="w-4 h-4" />} {label}
              </NavLink>
            ))}
            <div className="flex items-center gap-3 pt-2 border-t border-white/5">
              {!isLoggedIn && (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="flex-1 text-center text-sm text-gray-300 border border-dark-500 px-4 py-2 rounded-xl"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register?plan=starter&path=trial"
                    onClick={() => setOpen(false)}
                    className="flex-1 text-center text-sm font-semibold bg-amber-400 text-gray-900 px-4 py-2 rounded-xl"
                  >
                    Start Free Trial
                  </Link>
                </>
              )}
              {isLoggedIn && isPending && (
                <>
                  <button
                    type="button"
                    onClick={() => handleLogout(true)}
                    className="flex-1 text-center text-sm text-gray-300 border border-dark-500 px-4 py-2 rounded-xl"
                  >
                    Sign out
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePickPlan(true)}
                    className="flex-1 text-center text-sm font-semibold bg-amber-400 text-gray-900 px-4 py-2 rounded-xl"
                  >
                    {onPricing ? 'Pick a Plan' : 'Complete Subscription'}
                  </button>
                </>
              )}
              {isLoggedIn && !isPending && (
                <>
                  <button
                    type="button"
                    onClick={() => handleLogout(true)}
                    className="flex-1 text-center text-sm text-gray-300 border border-dark-500 px-4 py-2 rounded-xl"
                  >
                    Sign out
                  </button>
                  <Link
                    to="/"
                    onClick={() => setOpen(false)}
                    className="flex-1 text-center text-sm font-semibold bg-amber-400 text-gray-900 px-4 py-2 rounded-xl"
                  >
                    Dashboard
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
